function normalizeRoutePath(pathname: string): string {
  if (!pathname || pathname === '/') {
    return '/';
  }

  const withLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return withLeadingSlash.replace(/\/{2,}/g, '/');
}

const INTERNAL_ROUTE_BASE_URL = 'https://business.kalonmoves.com';
const TRUSTED_WEB_LINK_HOSTS = new Set([
  'business.kalonmoves.com',
  'vitalworkflow.app',
  'localhost',
  '127.0.0.1',
]);
const TRUSTED_NATIVE_LINK_PROTOCOLS = new Set([
  'com.kalonmoves.business:',
  'kalonmoves-business:',
  'vitalworkflow:',
]);
const UNSAFE_PATH_ENCODING_PATTERN = /\\|%(?:0[0-9a-f]|1[0-9a-f]|2f|5c|7f)/i;

function containsControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0);
    return codePoint !== undefined && (codePoint <= 0x1f || codePoint === 0x7f);
  });
}

export const NATIVE_SMOKE_ROUTE_KEY = '__vw_native_smoke_route';
export const NATIVE_SMOKE_ROUTE_ACK_KEY = '__vw_native_smoke_route_ack';
export const NATIVE_SMOKE_ACTIVE_KEY = '__vw_native_smoke_active';

export function resolveInternalAppRoute(rawValue: string): string | null {
  const route = rawValue.trim();
  if (!route.startsWith('/') || route.startsWith('//') || containsControlCharacter(route)) {
    return null;
  }

  const path = route.split(/[?#]/, 1)[0];
  if (UNSAFE_PATH_ENCODING_PATTERN.test(path)) {
    return null;
  }

  try {
    const parsedRoute = new URL(route, INTERNAL_ROUTE_BASE_URL);
    if (parsedRoute.origin !== INTERNAL_ROUTE_BASE_URL) {
      return null;
    }

    return `${parsedRoute.pathname}${parsedRoute.search}${parsedRoute.hash}`;
  } catch {
    return null;
  }
}

export function normalizeSmokeRoute(rawValue: string): string | null {
  if (!rawValue) {
    return null;
  }

  return rawValue.trim().startsWith('/')
    ? resolveInternalAppRoute(rawValue)
    : resolveNativeAppRoute(rawValue);
}

export function resolveNativeAppRoute(rawUrl: string): string | null {
  try {
    const parsedUrl = new URL(rawUrl);
    const isWebUrl = parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';

    let pathname = '';

    if (isWebUrl) {
      if (!TRUSTED_WEB_LINK_HOSTS.has(parsedUrl.hostname)) {
        return null;
      }
      pathname = parsedUrl.pathname;
    } else if (TRUSTED_NATIVE_LINK_PROTOCOLS.has(parsedUrl.protocol) && parsedUrl.host) {
      const suffix = parsedUrl.pathname === '/' ? '' : parsedUrl.pathname;
      pathname = `/${parsedUrl.host}${suffix}`;
    } else {
      return null;
    }

    const normalizedPath = normalizeRoutePath(pathname);
    return resolveInternalAppRoute(`${normalizedPath}${parsedUrl.search}${parsedUrl.hash}`);
  } catch {
    return null;
  }
}
